import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { buildApiUrl } from '../../services/api';
import type { CrmStageKey } from '../../types/crm';
import { applyWhatsAppTemplateVariables, buildWhatsAppDeepLink } from '../../utils/whatsappTemplate';

type TemplateChannel = 'WHATSAPP' | 'EMAIL' | 'COPY';

interface CrmMessageTemplate {
  id: string;
  name: string;
  category: string;
  messageType: 'TEXT';
  content: string;
  active: boolean;
  tags: string[];
  description?: string | null;
  kanbanStages?: CrmStageKey[];
  channels?: TemplateChannel[];
  insuranceTypes?: string[];
  sortOrder?: number;
  usageCount?: number;
  lastUsedAt?: string | null;
  isFavorite?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TemplatePreviewContext {
  nome: string;
  primeiro_nome: string;
  corretor: string;
  empresa: string;
  telefone: string;
  email: string;
  seguro: string;
  seguradora: string;
  placa: string;
  modelo: string;
  cidade: string;
  documentos: string;
  valor: string;
  link: string;
  numero_apolice: string;
  data_vencimento: string;
  responsavel: string;
  etapa_funil: string;
}

interface TemplateFormState {
  id: string | null;
  name: string;
  category: string;
  description: string;
  content: string;
  active: boolean;
  isFavorite: boolean;
  sortOrder: number;
  kanbanStages: CrmStageKey[];
  channels: TemplateChannel[];
  insuranceTypes: string[];
}

const TEMPLATE_CATEGORIES = [
  'Primeiro Contato',
  'Qualificação',
  'Follow-up',
  'Cotação',
  'Proposta',
  'Negociação',
  'Documentação',
  'Aprovação',
  'Apólice',
  'Pós-venda',
  'Renovação',
  'Sinistro',
  'Reativação',
  'Campanhas',
  'Aniversário'
];

const EXTRA_TEMPLATE_CATEGORIES = [
  'Cotacao',
  'Proposta',
  'Negociacao',
  'Documentacao',
  'Aprovacao',
  'Apolice Emitida',
  'Pos-venda',
  'Renovacao',
  'Sinistro',
  'Reativacao',
  'Negocio Perdido'
];

const ALL_TEMPLATE_CATEGORIES = [...TEMPLATE_CATEGORIES, ...EXTRA_TEMPLATE_CATEGORIES];

const STAGE_OPTIONS: Array<{ value: CrmStageKey; label: string }> = [
  { value: 'NOVO_CONTATO', label: 'Novo Contato' },
  { value: 'IDENTIFICADO', label: 'Identificado' },
  { value: 'CONTATO_FEITO', label: 'Contato Feito' },
  { value: 'INTERESSADO', label: 'Interessado' },
  { value: 'EM_ACOMPANHAMENTO', label: 'Cotação em Andamento' },
  { value: 'CONVERTIDO', label: 'Aprovado' },
  { value: 'SEM_RESPOSTA', label: 'Sem Resposta' },
  { value: 'SEM_INTERESSE', label: 'Negócio Perdido' },
  { value: 'INATIVO', label: 'Cliente Inativo' }
];

const CHANNEL_OPTIONS: Array<{ value: TemplateChannel; label: string }> = [
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'COPY', label: 'Copiar' }
];

const INSURANCE_OPTIONS = [
  'Automóvel',
  'Vida',
  'Saúde',
  'Residencial',
  'Empresarial',
  'Moto',
  'Frota',
  'Condomínio',
  'Viagem',
  'Geral'
];

const VARIABLE_BUTTONS = [
  '{{nome}}',
  '{{primeiro_nome}}',
  '{{corretor}}',
  '{{empresa}}',
  '{{telefone}}',
  '{{email}}',
  '{{seguro}}',
  '{{seguradora}}',
  '{{placa}}',
  '{{modelo}}',
  '{{cidade}}',
  '{{documentos}}',
  '{{valor}}',
  '{{link}}',
  '{{numero_apolice}}',
  '{{data_vencimento}}',
  '{{responsavel}}',
  '{{etapa_funil}}'
];

const createInitialForm = (): TemplateFormState => ({
  id: null,
  name: '',
  category: ALL_TEMPLATE_CATEGORIES[0],
  description: '',
  content: '',
  active: true,
  isFavorite: false,
  sortOrder: 0,
  kanbanStages: ['NOVO_CONTATO'],
  channels: ['WHATSAPP', 'EMAIL', 'COPY'],
  insuranceTypes: ['Geral']
});

const createPreviewContext = (): TemplatePreviewContext => ({
  nome: '',
  primeiro_nome: '',
  corretor: '',
  empresa: '',
  telefone: '',
  email: '',
  seguro: '',
  seguradora: '',
  placa: '',
  modelo: '',
  cidade: '',
  documentos: '',
  valor: '',
  link: '',
  numero_apolice: '',
  data_vencimento: '',
  responsavel: '',
  etapa_funil: ''
});

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Sem registro';
  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const normalize = (value: string) => value.trim().toLocaleLowerCase('pt-BR');

export function CrmMessageTemplatesPanel() {
  const [templates, setTemplates] = useState<CrmMessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState(createInitialForm);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    stage: '',
    channel: '',
    insuranceType: ''
  });
  const [previewContext, setPreviewContext] = useState<TemplatePreviewContext>(createPreviewContext);

  const authenticatedFetch = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(buildApiUrl(endpoint), {
      ...options,
      headers
    });
  }, []);

  const loadTemplates = useCallback(async () => {
    const params = new URLSearchParams({
      messageType: 'TEXT'
    });

    const response = await authenticatedFetch(`/templates?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const payload = await response.json();
    return (payload.data || []) as CrmMessageTemplate[];
  }, [authenticatedFetch]);

  const refreshTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const loadedTemplates = await loadTemplates();
      setTemplates(loadedTemplates);
      setSelectedTemplateId((current) => current || loadedTemplates[0]?.id || '');
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setTemplates([]);
      toast.error('Nao foi possivel carregar a biblioteca de templates');
    } finally {
      setLoading(false);
    }
  }, [loadTemplates]);

  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const filteredTemplates = useMemo(() => {
    return templates
      .filter((template) => {
        if (filters.search) {
          const search = normalize(filters.search);
          const haystack = [
            template.name,
            template.description || '',
            template.content,
            template.category,
            ...(template.kanbanStages || []),
            ...(template.channels || []),
            ...(template.insuranceTypes || [])
          ].join(' ').toLocaleLowerCase('pt-BR');
          if (!haystack.includes(search)) {
            return false;
          }
        }

        if (filters.category && template.category !== filters.category) return false;
        if (filters.stage && !(template.kanbanStages || []).includes(filters.stage as CrmStageKey)) return false;
        if (filters.channel && !(template.channels || []).includes(filters.channel as TemplateChannel)) return false;
        if (filters.insuranceType && !(template.insuranceTypes || []).some((type) => normalize(type) === normalize(filters.insuranceType))) return false;

        return true;
      })
      .sort((left, right) => {
        if ((left.isFavorite ? 1 : 0) !== (right.isFavorite ? 1 : 0)) {
          return Number(right.isFavorite) - Number(left.isFavorite);
        }

        const order = (left.sortOrder || 0) - (right.sortOrder || 0);
        if (order !== 0) return order;

        return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
      });
  }, [filters, templates]);

  const selectedTemplate = useMemo(
    () => filteredTemplates.find((template) => template.id === selectedTemplateId) || filteredTemplates[0] || null,
    [filteredTemplates, selectedTemplateId]
  );

  useEffect(() => {
    if (!selectedTemplateId && filteredTemplates[0]?.id) {
      setSelectedTemplateId(filteredTemplates[0].id);
    }
  }, [filteredTemplates, selectedTemplateId]);

  const resetForm = () => {
    setForm(createInitialForm());
    setIsFormOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error('Informe um nome para o template');
      return;
    }

    if (!form.content.trim()) {
      toast.error('Informe a mensagem do template');
      return;
    }

    if (form.kanbanStages.length === 0) {
      toast.error('Selecione ao menos uma etapa do CRM');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        category: form.category,
        messageType: 'TEXT',
        content: form.content,
        active: form.active,
        isFavorite: form.isFavorite,
        tags: ['crm', 'seguros', form.category.toLowerCase().replace(/\s+/g, '-')],
        description: form.description.trim(),
        isShared: false,
        kanbanStages: form.kanbanStages,
        channels: form.channels,
        insuranceTypes: form.insuranceTypes,
        sortOrder: Number(form.sortOrder) || 0
      };

      const endpoint = form.id ? `/templates/${form.id}` : '/templates';
      const method = form.id ? 'PUT' : 'POST';
      const response = await authenticatedFetch(endpoint, {
        method,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || `HTTP ${response.status}`);
      }

      toast.success(form.id ? 'Template atualizado com sucesso' : 'Template criado com sucesso');
      resetForm();
      await refreshTemplates();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    try {
      setDeletingId(templateId);
      const response = await authenticatedFetch(`/templates/${templateId}`, { method: 'DELETE' });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || `HTTP ${response.status}`);
      }

      toast.success('Template excluido');
      if (form.id === templateId) resetForm();
      await refreshTemplates();
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir template');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDuplicate = async (template: CrmMessageTemplate) => {
    const newName = window.prompt('Nome para a cópia do template', `${template.name} - cópia`);
    if (!newName?.trim()) return;

    try {
      setDuplicatingId(template.id);
      const response = await authenticatedFetch(`/templates/${template.id}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim() })
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || `HTTP ${response.status}`);
      }

      toast.success('Template duplicado');
      await refreshTemplates();
    } catch (error) {
      console.error('Erro ao duplicar template:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao duplicar template');
    } finally {
      setDuplicatingId(null);
    }
  };

  const incrementUsage = useCallback(async (templateId: string, channel: TemplateChannel) => {
    await authenticatedFetch(`/templates/${templateId}/use`, {
      method: 'POST',
      body: JSON.stringify({ channel })
    });
    await refreshTemplates();
  }, [authenticatedFetch, refreshTemplates]);

  const handleCopy = async (template: CrmMessageTemplate) => {
    const copiedText = applyWhatsAppTemplateVariables(template.content, previewContext);
    try {
      await navigator.clipboard.writeText(copiedText);
      toast.success('Texto copiado');
      await incrementUsage(template.id, 'COPY');
    } catch (error) {
      toast.error('Nao foi possivel copiar o texto');
    }
  };

  const handleWhatsApp = async (template: CrmMessageTemplate) => {
    if (!previewContext.telefone.trim()) {
      toast.error('Informe o telefone do cliente na área de pré-visualização.');
      return;
    }

    const message = applyWhatsAppTemplateVariables(template.content, previewContext);
    const url = buildWhatsAppDeepLink(previewContext.telefone, message);
    if (!url) {
      toast.error('Telefone inválido para WhatsApp');
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
    await incrementUsage(template.id, 'WHATSAPP');
  };

  const handleEmail = async (template: CrmMessageTemplate) => {
    if (!previewContext.email.trim()) {
      toast.error('Informe o e-mail do cliente na área de pré-visualização.');
      return;
    }

    const subject = encodeURIComponent(template.name);
    const body = encodeURIComponent(applyWhatsAppTemplateVariables(template.content, previewContext));
    window.location.href = `mailto:${previewContext.email}?subject=${subject}&body=${body}`;
    await incrementUsage(template.id, 'EMAIL');
  };

  const handleFavorite = async (template: CrmMessageTemplate) => {
    try {
      const response = await authenticatedFetch(`/templates/${template.id}/favorite`, {
        method: 'PATCH',
        body: JSON.stringify({ isFavorite: !template.isFavorite })
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || `HTTP ${response.status}`);
      }

      await refreshTemplates();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar favorito');
    }
  };

  const handleEdit = (template: CrmMessageTemplate) => {
    setForm({
      id: template.id,
      name: template.name,
      category: template.category,
      description: template.description || '',
      content: template.content,
      active: template.active,
      isFavorite: template.isFavorite || false,
      sortOrder: template.sortOrder || 0,
      kanbanStages: (template.kanbanStages || []) as CrmStageKey[],
      channels: (template.channels || ['WHATSAPP', 'EMAIL', 'COPY']) as TemplateChannel[],
      insuranceTypes: template.insuranceTypes || ['Geral']
    });
    setIsFormOpen(true);
  };

  const selectedStageLabel = selectedTemplate?.kanbanStages?.map((stage) => STAGE_OPTIONS.find((option) => option.value === stage)?.label || stage).join(', ') || 'Sem etapa';

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#1d4ed8_55%,#22c55e_100%)] p-5 text-white shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">Biblioteca</p>
            <h3 className="mt-1 text-2xl font-semibold">Templates de Mensagens</h3>
            <p className="mt-1 max-w-3xl text-sm text-white/75">
              Crie, classifique e reutilize mensagens para WhatsApp, e-mail ou cópia direta, com vínculo por etapa do CRM.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setForm(createInitialForm());
              setIsFormOpen(true);
            }}
            className="rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
          >
            Novo Template
          </button>
        </div>
      </div>

      <section className="grid gap-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[1.2fr_0.9fr_0.9fr]">
          <input
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            placeholder="Pesquisar por nome ou texto"
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
          />
          <select
            value={filters.category}
            onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
          >
            <option value="">Todas as categorias</option>
            {ALL_TEMPLATE_CATEGORIES.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <select
            value={filters.stage}
            onChange={(event) => setFilters((prev) => ({ ...prev, stage: event.target.value }))}
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
          >
            <option value="">Todas as etapas</option>
            {STAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowAdvanced((prev) => !prev)}
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            {showAdvanced ? 'Ocultar filtros avançados' : 'Mostrar filtros avançados'}
          </button>
        </div>

        {showAdvanced ? (
          <div className="grid gap-3 rounded-3xl bg-slate-50 p-4 lg:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Canal</span>
              <select
                value={filters.channel}
                onChange={(event) => setFilters((prev) => ({ ...prev, channel: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-slate-400"
              >
                <option value="">Todos os canais</option>
                {CHANNEL_OPTIONS.map((channel) => (
                  <option key={channel.value} value={channel.value}>{channel.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Tipo de seguro</span>
              <select
                value={filters.insuranceType}
                onChange={(event) => setFilters((prev) => ({ ...prev, insuranceType: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-slate-400"
              >
                <option value="">Todos os tipos</option>
                {INSURANCE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
      </section>

      {loading ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
          Carregando biblioteca...
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
          Nenhum template encontrado.
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2 2xl:grid-cols-3">
          {filteredTemplates.map((template) => (
            <article
              key={template.id}
              className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedTemplateId(template.id);
                    handleEdit(template);
                  }}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-semibold text-slate-950">{template.name}</h4>
                    {template.isFavorite ? <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-800">Favorito</span> : null}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{template.category}</p>
                </button>
                <button
                  type="button"
                  onClick={() => handleFavorite(template)}
                  className={`rounded-full border px-3 py-2 text-xs font-semibold transition ${
                    template.isFavorite ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-slate-200 bg-white text-slate-600'
                  }`}
                >
                  {template.isFavorite ? '★' : '☆'}
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold">
                {(template.kanbanStages || []).map((stage) => (
                  <span key={stage} className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
                    {STAGE_OPTIONS.find((option) => option.value === stage)?.label || stage}
                  </span>
                ))}
                {(template.channels || []).map((channel) => (
                  <span key={channel} className="rounded-full bg-slate-100 px-3 py-1 text-slate-600">
                    {channel}
                  </span>
                ))}
                {(template.insuranceTypes || []).map((type) => (
                  <span key={type} className="rounded-full bg-blue-50 px-3 py-1 text-blue-700">
                    {type}
                  </span>
                ))}
              </div>

              <div className="mt-4 rounded-2xl bg-slate-50 p-4">
                <p
                  className="text-sm leading-6 text-slate-600"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {applyWhatsAppTemplateVariables(template.content, previewContext)}
                </p>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-slate-500">
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-slate-400">Utilizações</span>
                  <span>{template.usageCount || 0}</span>
                </div>
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-slate-400">Última edição</span>
                  <span>{formatDateTime(template.updatedAt)}</span>
                </div>
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-slate-400">Canal</span>
                  <span>{(template.channels || []).join(', ') || 'Não definido'}</span>
                </div>
                <div>
                  <span className="block text-[11px] uppercase tracking-wide text-slate-400">Etapa</span>
                  <span>{(template.kanbanStages || []).length ? (template.kanbanStages || []).map((stage) => STAGE_OPTIONS.find((option) => option.value === stage)?.label || stage).join(', ') : 'Sem etapa'}</span>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <button type="button" onClick={() => handleEdit(template)} className="rounded-2xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Editar</button>
                <button type="button" onClick={() => handleDuplicate(template)} disabled={duplicatingId === template.id} className="rounded-2xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">{duplicatingId === template.id ? 'Duplicando...' : 'Duplicar'}</button>
                <button type="button" onClick={() => handleCopy(template)} className="rounded-2xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Copiar</button>
                <button type="button" onClick={() => handleWhatsApp(template)} className="rounded-2xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">WhatsApp</button>
                <button type="button" onClick={() => handleEmail(template)} className="rounded-2xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">E-mail</button>
                <button type="button" onClick={() => handleDelete(template.id)} disabled={deletingId === template.id} className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60">{deletingId === template.id ? 'Excluindo...' : 'Excluir'}</button>
              </div>
            </article>
          ))}
        </div>
      )}

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h4 className="text-sm font-semibold text-slate-950">Dados de teste</h4>
            <p className="text-xs text-slate-500">Use estes campos para testar a mensagem antes de enviar, copiar ou abrir o canal.</p>
          </div>
          {selectedTemplate ? <p className="text-xs text-slate-500">Selecionado: {selectedTemplate.name}</p> : null}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[
            ['nome', 'Nome'],
            ['corretor', 'Corretor'],
            ['empresa', 'Empresa'],
            ['seguro', 'Seguro'],
            ['seguradora', 'Seguradora'],
            ['telefone', 'Telefone'],
            ['email', 'E-mail'],
            ['etapa_funil', 'Etapa do funil']
          ].map(([key, label]) => (
            <label key={key} className="block">
              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
              <input
                value={(previewContext as any)[key]}
                onChange={(event) => setPreviewContext((prev) => ({ ...prev, [key]: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-slate-400"
              />
            </label>
          ))}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
            <span className="block text-[11px] uppercase tracking-wide text-slate-400">Etapa</span>
            {selectedStageLabel}
          </div>
          <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
            <span className="block text-[11px] uppercase tracking-wide text-slate-400">Template selecionado</span>
            {selectedTemplate?.name || 'Nenhum'}
          </div>
          <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
            <span className="block text-[11px] uppercase tracking-wide text-slate-400">Categoria</span>
            {selectedTemplate?.category || 'Nenhuma'}
          </div>
        </div>
      </section>

      {isFormOpen ? (
        <form onSubmit={handleSubmit} className="space-y-5 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-950">{form.id ? 'Editar template' : 'Novo template'}</h4>
              <p className="mt-1 text-xs text-slate-500">Cada template pode ser vinculado a uma ou mais etapas do CRM e a mais de um canal.</p>
            </div>
            <button type="button" onClick={resetForm} className="rounded-2xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">Fechar</button>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Nome</span>
              <input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-slate-500" />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Categoria</span>
              <select value={form.category} onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-slate-500">
                {!ALL_TEMPLATE_CATEGORIES.includes(form.category) ? (
                  <option value={form.category}>{form.category}</option>
                ) : null}
                {ALL_TEMPLATE_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
            </label>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Descrição</span>
              <input value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-slate-500" />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Ordem de exibição</span>
              <input type="number" min={0} value={form.sortOrder} onChange={(event) => setForm((prev) => ({ ...prev, sortOrder: Number(event.target.value) || 0 }))} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-slate-500" />
            </label>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-slate-700">Conteúdo</span>
            <textarea value={form.content} onChange={(event) => setForm((prev) => ({ ...prev, content: event.target.value }))} rows={10} className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-slate-500" />
          </label>

          <div>
            <p className="mb-2 block text-sm font-medium text-slate-700">Etapas</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {STAGE_OPTIONS.map((stage) => {
                const checked = form.kanbanStages.includes(stage.value);
                return (
                  <label key={stage.value} className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm ${checked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                    <input type="checkbox" checked={checked} onChange={(event) => setForm((prev) => ({
                      ...prev,
                      kanbanStages: event.target.checked ? [...prev.kanbanStages, stage.value] : prev.kanbanStages.filter((value) => value !== stage.value)
                    }))} />
                    <span>{stage.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-2 block text-sm font-medium text-slate-700">Canais</p>
            <div className="flex flex-wrap gap-3">
              {CHANNEL_OPTIONS.map((channel) => {
                const checked = form.channels.includes(channel.value);
                return (
                  <label key={channel.value} className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-4 py-3 text-sm ${checked ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'}`}>
                    <input type="checkbox" checked={checked} onChange={(event) => setForm((prev) => ({
                      ...prev,
                      channels: event.target.checked ? [...prev.channels, channel.value] : prev.channels.filter((value) => value !== channel.value)
                    }))} />
                    <span>{channel.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-2 block text-sm font-medium text-slate-700">Tipos de seguro</p>
            <div className="flex flex-wrap gap-3">
              {INSURANCE_OPTIONS.map((type) => {
                const checked = form.insuranceTypes.includes(type);
                return (
                  <label key={type} className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-4 py-3 text-sm ${checked ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white'}`}>
                    <input type="checkbox" checked={checked} onChange={(event) => setForm((prev) => ({
                      ...prev,
                      insuranceTypes: event.target.checked ? [...prev.insuranceTypes, type] : prev.insuranceTypes.filter((value) => value !== type)
                    }))} />
                    <span>{type}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.active} onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))} />
              Template ativo
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.isFavorite} onChange={(event) => setForm((prev) => ({ ...prev, isFavorite: event.target.checked }))} />
              Favorito
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            {VARIABLE_BUTTONS.map((variable) => (
              <button key={variable} type="button" onClick={() => setForm((prev) => ({ ...prev, content: `${prev.content}${variable}` }))} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200">
                {variable}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={saving} className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
              {saving ? 'Salvando...' : form.id ? 'Atualizar template' : 'Salvar template'}
            </button>
            {form.id ? (
              <button type="button" onClick={() => handleDelete(form.id!)} className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100">
                Excluir
              </button>
            ) : null}
          </div>
        </form>
      ) : null}
    </div>
  );
}
